import { cn } from '@/lib/cn';

interface CassetteReelsProps {
  spinning?: boolean;
  className?: string;
  /** Accessible label; omit for decorative use. */
  label?: string;
  labelColor?: 'orange' | 'pink' | 'teal';
}

function Reel({ cx, spinning }: { cx: number; spinning: boolean }) {
  return (
    <g
      className={cn(spinning && 'animate-reel motion-reduce:animate-none')}
      style={{ transformOrigin: `${cx}px 44px`, transformBox: 'view-box' }}
    >
      <circle cx={cx} cy={44} r={13} fill="var(--mx-paper)" />
      <circle cx={cx} cy={44} r={5} fill="var(--mx-deck-800)" />
      {[0, 60, 120, 180, 240, 300].map((deg) => (
        <rect
          key={deg}
          x={cx - 1.4}
          y={44 - 12}
          width={2.8}
          height={6}
          rx={1}
          fill="var(--mx-deck-800)"
          transform={`rotate(${deg} ${cx} 44)`}
        />
      ))}
    </g>
  );
}

/** A cassette shell whose reels spin while something plays (or loads). */
export function CassetteReels({
  spinning = false,
  className,
  label,
  labelColor = 'orange',
}: CassetteReelsProps) {
  const stripe = { orange: 'var(--mx-orange)', pink: 'var(--mx-pink)', teal: 'var(--mx-teal)' }[
    labelColor
  ];
  return (
    <svg
      viewBox="0 0 160 100"
      className={cn('h-auto w-40', className)}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      <rect x="2" y="2" width="156" height="96" rx="10" fill="var(--mx-deck-600)" />
      <rect x="2" y="2" width="156" height="96" rx="10" fill="none" stroke="rgb(0 0 0 / .35)" />
      {/* Label */}
      <rect x="12" y="10" width="136" height="62" rx="5" fill="var(--mx-paper)" />
      <rect x="12" y="14" width="136" height="7" fill={stripe} />
      <line x1="20" y1="30" x2="140" y2="30" stroke="var(--mx-paper-line)" />
      {/* Window */}
      <rect x="34" y="31" width="92" height="26" rx="13" fill="var(--mx-deck-900)" />
      <rect x="62" y="36" width="36" height="16" rx="3" fill="var(--mx-deck-700)" opacity=".8" />
      <Reel cx={52} spinning={spinning} />
      <Reel cx={108} spinning={spinning} />
      {/* Bottom trapezoid */}
      <path d="M36 98l6-18h76l6 18" fill="var(--mx-deck-700)" />
      <circle cx="58" cy="89" r="3" fill="var(--mx-deck-900)" />
      <circle cx="102" cy="89" r="3" fill="var(--mx-deck-900)" />
    </svg>
  );
}
